import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { GpsIntegrationsQueryData } from '@area-restrita/api-client/model/gpsIntegrationsQueryData';
import { UpdateGpsIntegrationBody } from '@area-restrita/api-client/model/updateGpsIntegrationBody';

import { Button } from '@/components/ui/button';
import { Form, FormLabel } from '@/components/ui/form';

import { getProviderSchema, gpsProviderConfigs } from './gpsProviderConfigs';
import GpsConfigFields from './GpsConfigFields';

interface EditGpsIntegrationFormProps {
  integration: GpsIntegrationsQueryData;
  onSubmit: (data: UpdateGpsIntegrationBody) => void;
  isLoading?: boolean;
}

// Schema base do formulário de edição
const baseEditFormSchema = z.object({
  config: z.record(z.any()),
});

type EditFormData = z.infer<typeof baseEditFormSchema>;

const EditGpsIntegrationForm: React.FC<EditGpsIntegrationFormProps> = ({
  integration,
  onSubmit,
  isLoading = false,
}) => {
  const form = useForm<EditFormData>({
    resolver: zodResolver(baseEditFormSchema),
    defaultValues: {
      config: integration.config || {},
    },
  });

  const handleSubmit = (data: EditFormData) => {
    // Valida a configuração específica do provedor
    const providerSchema = getProviderSchema(integration.gpsProvider);
    const validatedConfig = providerSchema.parse(data.config);

    const payload: UpdateGpsIntegrationBody = {
      gpsProvider: integration.gpsProvider,
      config: validatedConfig,
      priority: integration.priority, // Mantém a prioridade original
    };

    onSubmit(payload);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="mb-4">
          <FormLabel>Provedor GPS</FormLabel>
          <div className="mt-1 p-2 bg-muted rounded-md">
            <span className="text-sm font-medium">
              {gpsProviderConfigs[integration.gpsProvider]?.label ||
                integration.gpsProvider}
            </span>
            <p className="text-xs text-muted-foreground mt-1">
              O tipo de provedor não pode ser alterado
            </p>
          </div>
        </div>

        <GpsConfigFields
          control={form.control}
          provider={integration.gpsProvider}
          configFieldName="config"
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EditGpsIntegrationForm;
